import { useState, useCallback, useMemo } from 'react';
import { EarlyWarning } from '@/types/early-warning';

type SortDirection = 'asc' | 'desc';

interface UseEarlyWarningFiltersReturn {
  sortField: string;
  sortDirection: SortDirection;
  statusFilter: string;
  handleSort: (field: string) => void;
  setStatusFilter: (value: string) => void;
  filteredData: EarlyWarning[];
}

const IMPACT_FIELDS = [
  'increasePrices',
  'delayCompletion',
  'delayKeyDate',
  'impairPerformance',
  'increaseContractorCost',
] as const;

type ImpactField = (typeof IMPACT_FIELDS)[number];

function getFieldValue(warning: EarlyWarning, field: string): unknown {
  if (IMPACT_FIELDS.includes(field as ImpactField)) {
    return warning.impacts
      ? warning.impacts[field as keyof typeof warning.impacts]
      : false;
  }
  return warning[field as keyof EarlyWarning];
}

export function useEarlyWarningFilters(
  warningData: EarlyWarning[]
): UseEarlyWarningFiltersReturn {
  const [sortField, setSortField] = useState('id');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [statusFilter, setStatusFilter] = useState('all');

  const handleSort = useCallback(
    (field: string) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField, sortDirection]
  );

  const sortedData = useMemo(() => {
    return [...warningData].sort((a, b) => {
      const aVal = getFieldValue(a, sortField);
      const bVal = getFieldValue(b, sortField);
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [warningData, sortField, sortDirection]);

  const filteredData = useMemo(() => {
    return sortedData.filter((warning) =>
      statusFilter === 'all'
        ? true
        : warning.status.toLowerCase() === statusFilter
    );
  }, [sortedData, statusFilter]);

  return {
    sortField,
    sortDirection,
    statusFilter,
    handleSort,
    setStatusFilter,
    filteredData,
  };
}
